import { getLocalTimeZone, today } from '@internationalized/date'
import { Database01, FaceContent, SearchMd, User01 } from '@untitledui/icons'
import { useRef, useState } from 'react'
import { useListData } from 'react-stately'

import Button from '@components/designSystem/Button'
import ButtonGroup from '@components/designSystem/ButtonGroup'
import Card from '@components/designSystem/Card'
import Dialog from '@components/designSystem/Dialog/Dialog'
import { useAlertDialog, useConfirmDialog, useDialog } from '@components/designSystem/Dialog/useDialog'
import { Headline } from '@components/designSystem/Headline'
import { PopoverTrigger, Popover, PopoverContent } from '@components/designSystem/Popover'
import { RadioButton, RadioGroup } from '@components/designSystem/Radio'
import Tooltip from '@components/designSystem/Tooltip'
import {
  Badge,
  BadgeIcon,
  BadgeWithButton,
  BadgeWithDot, BadgeWithFlag,
  BadgeWithIcon,
  BadgeWithImage
} from '@components/designSystem/badges/badges'
import { DatePicker } from '@components/designSystem/date-picker/date-picker'
import { DateRangePicker } from '@components/designSystem/date-picker/date-range-picker'
import { Input } from '@components/designSystem/input/input'
import { MultiSelect } from '@components/designSystem/select/multi-select'
import { Select } from '@components/designSystem/select/select'
import HorizontalScrollerDemo from '@routes/Components/HorizontalScrollerDemo'
import TabsDemo from '@routes/Components/TabsDemo'
import ToastDemo from '@routes/Components/ToastDemo'

import type { BaseDialogProps } from '@components/designSystem/Dialog/DialogContext'
import type { SelectItemType } from '@components/designSystem/select/select'
import type { DateValue } from 'react-aria-components'

// Custom Dialog Component for examples
interface CustomDialogProps extends BaseDialogProps {
  userName: string
  userEmail: string
}

const items = [
  { label: 'Phoenix Baker', id: '@phoenix', supportingText: '@phoenix' },
  { label: 'Olivia Rhye', id: '@olivia', supportingText: '@olivia' },
  { label: 'Lana Steiner', id: '@lana', supportingText: '@lana', disabled: true },
  { label: 'Demi Wilkinson', id: '@demi', supportingText: '@demi' },
  { label: 'Candice Wu', id: '@candice', supportingText: '@candice' },
  { label: 'Natali Craig', id: '@natali', supportingText: '@natali' },
  { label: 'Abraham Baker', id: '@abraham', supportingText: '@abraham' },
  { label: 'Adem Lane', id: '@adem', supportingText: '@adem' },
  { label: 'Jackson Reed', id: '@jackson', supportingText: '@jackson' },
  { label: 'Jessie Meyton', id: '@jessie', supportingText: '@jessie' },
]

const now = today(getLocalTimeZone())

function CustomUserDialog({ userName, userEmail, onClose }: CustomDialogProps) {
  return (
    <Dialog
      headerIcon={User01}
      title="User Information"
      description="Review the user details below"
      onClose={onClose}
      footer={
        <>
          <Button>Cancel</Button>
          <Button onClick={() => {
            console.log('User confirmed')
            onClose?.()
          }}
          >
            Save Changes
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Username" defaultValue={userName} />
        <Input label="Email" type="email" defaultValue={userEmail} />
      </div>
    </Dialog>
  )
}

/**
 * This route is only rendered in the dev environment.
 * It is used to test and showcase the components in isolation.
 * It is not meant to be used in production.
 */
function ComponentsRoute() {
  const [manualPopoverOpen, setManualPopoverOpen] = useState(false)
  const triggerRef = useRef(null)

  const [date, setDate] = useState<DateValue | null>(now)

  const [range, setRange] = useState<{ start: DateValue; end: DateValue } | null>({
    start: now.subtract({ days: 7 }),
    end: now,
  })

  // Radio component state
  const [selectedRadio, setSelectedRadio] = useState('option1')
  const [selectedColor, setSelectedColor] = useState('blue')

  // Button Group state
  const [selectedButton, setSelectedButton] = useState<'A' | 'B' | 'C'>('A')

  const list = useListData<SelectItemType>({
    initialItems: [],
    initialSelectedKeys: ['Kangaroo'],
    getKey: item => item.id,
  })

  // Dialog hooks
  const userDialog = useDialog(CustomUserDialog, {
    userName: 'John Doe',
    userEmail: 'john@example.com',
  })
  const confirmDialog = useConfirmDialog()
  const alertDialog = useAlertDialog()

  const handleDeleteItem = () => {
    confirmDialog.showDialog({
      title: 'Delete Item',
      message: 'Are you sure you want to delete this item? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Keep',
      onConfirm: async () => {
        await new Promise((resolve) => setTimeout(resolve, 10_000)) // Simulate async operation
        console.log('Item deleted')
      },
    })
  }

  const handleShowInfo = () => {
    alertDialog.showDialog({
      title: 'Information',
      message: 'This is an informational message with some details about the current operation.',
      variant: 'info',
      buttonText: 'Got it',
    })
  }

  const handleShowWarning = () => {
    alertDialog.showDialog({
      title: 'Warning',
      message: 'Please be careful with this action. Make sure you have backed up your data.',
      variant: 'warning',
    })
  }

  return (
    <div className="flex flex-col items-center justify-center gap-8 py-10">
      <section>
        <h2 className="mb-2 text-lg font-bold">Button</h2>
        <div className="mb-4 flex gap-4">
          <Button variant="filled">Filled</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="gray">Gray</Button>
          <Button variant="dark">Dark</Button>
        </div>

        <div className="mb-4 flex gap-4">
          <Button variant="filled-destructive">Filled Destructive</Button>
          <Button variant="outlined-destructive">Outlined Destructive</Button>
          <Button variant="ghost-destructive">Ghost Destructive</Button>
        </div>

        <div className="mb-4 flex gap-4">
          <Button variant='filled' isDisabled>Filled disabled</Button>
          <Button variant="outline" isDisabled>Outline disabled</Button>
          <Button variant="ghost" isDisabled>Ghost disabled</Button>
          <Button variant="dark" isDisabled>Dark disabled</Button>
        </div>

        <div className="flex gap-4">
          <Button size='sm'>Small</Button>
          <Button size='md'>Medium</Button>
          <Button size='lg'>Large</Button>
          <Button size='xl'>Extra Large</Button>
        </div>

        <div className="flex gap-4">
          <Button variant='link-color'>Link Color</Button>
          <Button variant='link-gray'>Link Gray</Button>
          <Button variant='link-destructive'>Link Destructive</Button>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-lg font-bold">Buttons With Icons</h2>

        <div className="mb-4 flex gap-4">
          <Button size='sm' variant="outline" iconLeading={<User01 />}>Icon Leading</Button>
          <Button size='sm' variant="outline" iconTrailing={<User01 />}>Icon Trailing</Button>
          <Button size='sm' variant="outline" iconLeading={<User01/>} iconTrailing={<User01 />}>Both</Button>

          <Button
            size='sm'
            variant="outline"
            iconTrailing={<Badge size='sm'>123</Badge>}
          >
            Custom
          </Button>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-lg font-bold">Badge</h2>

        {/* Basic Badge Examples */}
        <div className="space-y-6">
          <div>
            <h3 className="mb-3 text-md font-semibold">Colors</h3>
            <div className="flex flex-wrap gap-4">
              <Badge>Default Badge</Badge>
              <Badge color="brand">Brand Badge</Badge>
              <Badge color="error">Error Badge</Badge>
              <Badge color="warning">Warning Badge</Badge>
              <Badge color="success">Success Badge</Badge>
              <Badge color="dark">Dark Badge</Badge>
              <Badge color="outOfShelf">Out of Shelf Badge</Badge>
            </div>
          </div>

          {/* Badge Types */}
          <div>
            <h3 className="mb-3 text-md font-semibold">Badge Types</h3>
            <div className="flex flex-wrap gap-4">
              <Badge type="pill-color" color="brand">Pill Badge</Badge>
              <Badge type="color" color="brand">Square Badge</Badge>
            </div>
          </div>

          {/* Badge Sizes */}
          <div>
            <h3 className="mb-3 text-md font-semibold">Badge Sizes</h3>
            <div className="flex flex-wrap items-center gap-4">
              <Badge size="sm" color="brand">Small</Badge>
              <Badge size="md" color="brand">Medium</Badge>
              <Badge size="lg" color="brand">Large</Badge>
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-md font-semibold">Badge Variations</h3>
            <div className="flex flex-wrap items-center gap-4">
              <BadgeWithDot color="success">Online</BadgeWithDot>
              <BadgeWithIcon iconLeading={User01} color="brand">User Badge</BadgeWithIcon>
              <BadgeWithImage imgSrc="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face">
                John Doe
              </BadgeWithImage>
              <BadgeWithButton
                color="brand"
                buttonLabel="Remove tag"
                onButtonClick={() => alert('Badge button clicked')}
              >
                Removable Tag
              </BadgeWithButton>
              <BadgeIcon icon={User01} color="brand" />
              <BadgeWithFlag flag='en'>With Flag</BadgeWithFlag>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-lg font-bold">Card</h2>
        <div className="flex gap-4">
          <Card>
            <p className="text-sm">This is a card component.</p>
          </Card>
        </div>
      </section>

      <section className='w-1/4'>
        <h2 className="mb-2 text-lg font-bold">Textfield</h2>
        <div className="flex flex-col gap-2">
          <Input placeholder="Default"/>
          <Input placeholder="Leading Icon" leadingIcon={{ icon: SearchMd }} />
          <Input placeholder="Trailing Icon" trailingIcons={[{ icon: FaceContent }]} />
          <Input placeholder="Two Trailing Icons" trailingIcons={[{ icon: FaceContent }, { icon: Database01 }]} />
          <hr />
          <Input size='sm' placeholder="small input" />
          <Input size='md' placeholder="medium input" />
          <Input size='lg' placeholder="large input" />
          <hr />
          <Input placeholder="Shortcut" shortcut={true} />
          <Input placeholder="Tooltip" tooltip='This is a tooltip' />
          <Input label='My Label' hint='This is a hint' />
        </div>
      </section>

      <section className='w-1/4'>
        <Headline title='Select'></Headline>
        <div className="flex flex-col gap-2">
          <Select
            isRequired
            label="Team member"
            tooltip="This is a tooltip"
            hint="This is a hint text to help user."
            placeholder="Select team member"
            items={items}
          >
            {(item) => (
              <Select.Item id={item.id} supportingText={item.supportingText} isDisabled={item.isDisabled} icon={item.icon} avatarUrl={item.avatarUrl}>
                {item.label}
              </Select.Item>
            )}
          </Select>
          <Select.ComboBox isRequired placeholder="Combobox" items={items}>
            {(item) => (
              <Select.Item id={item.id} supportingText={item.supportingText} isDisabled={item.isDisabled} icon={item.icon} avatarUrl={item.avatarUrl}>
                {item.label}
              </Select.Item>
            )}
          </Select.ComboBox>

          <MultiSelect isRequired placeholder="Multiselect" items={items} selectedItems={list}>
            {(item) => (
              <MultiSelect.Item id={item.id} supportingText={item.supportingText} isDisabled={item.isDisabled} icon={item.icon} avatarUrl={item.avatarUrl}>
                {item.label}
              </MultiSelect.Item>
            )}
          </MultiSelect>
        </div>
      </section>

      <section>
        <h2>Datepicker</h2>
        <DatePicker aria-label="Date picker" value={date} onChange={setDate} />
        <DateRangePicker aria-label="Date range picker" shouldCloseOnSelect={false} value={range} onChange={setRange} />
      </section>

      <section>
        <h2 className="mb-2 text-lg font-bold">Dialog</h2>
        <div className="grid max-w-2xl grid-cols-1 gap-4 md:grid-cols-2">
          <Button onClick={() => userDialog.showDialog()} variant="outline">
            Custom User Dialog
          </Button>

          <Button onClick={handleDeleteItem} variant="outline">
            Confirm Dialog (Danger)
          </Button>

          <Button onClick={handleShowInfo} variant="outline">
            Info Alert
          </Button>

          <Button onClick={handleShowWarning} variant="outline">
            Warning Alert
          </Button>

          <Button onClick={() => userDialog.showDialog({ userName: 'Jane Smith', userEmail: 'jane@example.com' })} variant="outline">
            Dynamic Props Dialog
          </Button>

          <Button
            onClick={() => alertDialog.showDialog({
              title: 'Error',
              message: 'Something went wrong. Please try again.',
              variant: 'error',
            })}
            variant="outline"
          >
            Error Alert
          </Button>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-lg font-bold">Radio</h2>
        <div className="space-y-6">
          {/* Basic Radio Group with React Aria */}
          <div>
            <h3 className="mb-3 text-md font-semibold">Basic Options (React Aria)</h3>
            <RadioGroup value={selectedRadio} onChange={setSelectedRadio}>
              <RadioButton value="option1" label="First Option" />
              <RadioButton value="option2" label="Second Option" />
              <RadioButton value="option3" label="Third Option" />
            </RadioGroup>
            <div className="mt-2 rounded-sm bg-gray-100 p-3 text-sm">
              <strong>Selected:</strong> {selectedRadio}
            </div>
          </div>

          {/* Radio with Disabled State and Different Sizes */}
          <div>
            <h3 className="mb-3 text-md font-semibold">With Disabled Options & Sizes</h3>
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm text-gray-600">Small size (default):</p>
                <RadioGroup value={selectedColor} onChange={setSelectedColor} size="sm">
                  <RadioButton value="blue" label="Blue (Available)" />
                  <RadioButton value="red" label="Red (Out of Stock)" isDisabled />
                  <RadioButton value="green" label="Green (Available)" />
                </RadioGroup>
              </div>

              <div>
                <p className="mb-2 text-sm text-gray-600">Medium size:</p>
                <RadioGroup value={selectedColor} onChange={setSelectedColor} size="md">
                  <RadioButton value="blue" label="Blue (Available)" hint="This color is in stock" />
                  <RadioButton value="red" label="Red (Out of Stock)" hint="Currently unavailable" isDisabled />
                  <RadioButton value="green" label="Green (Available)" hint="Limited quantity" />
                </RadioGroup>
              </div>
            </div>
            <div className="mt-2 rounded-sm bg-gray-100 p-3 text-sm">
              <strong>Selected Color:</strong> {selectedColor}
            </div>
          </div>

        </div>

        <div>
          <h3 className="mb-3 text-md font-semibold">ButtonGroup</h3>

          <ButtonGroup
            selectedValue={selectedButton}
            onChange={value => setSelectedButton(value)}
            entries={[
              { label: 'A', value: 'A' },
              { label: 'B', value: 'B' },
              { label: 'C', value: 'C' },
            ] as const}
          />
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-lg font-bold">Popover</h2>
        <div className="grid max-w-2xl grid-cols-1 gap-4 md:grid-cols-2">
          {/* Default */}
          <Popover>
            <Button variant="outline">Click to open</Button>
            <PopoverContent>
              <div>
                <p className="mb-2 text-sm font-medium">Click Trigger</p>
                <p className="text-xs text-gray-600">Opens on click, closes on outside click or escape</p>
              </div>
            </PopoverContent>
          </Popover>

          {/* Manual control */}
          <div className="flex items-center gap-2">
            <Button
              variant="filled"
              size="sm"
              onClick={() => setManualPopoverOpen(!manualPopoverOpen)}
            >
              {manualPopoverOpen ? 'Close' : 'Open'}
            </Button>

            <p ref={triggerRef}>I'll open on this text</p>
            <PopoverContent triggerRef={triggerRef} isOpen={manualPopoverOpen} onOpenChange={setManualPopoverOpen}>
              <div>
                <p className="text-xs">I was opened manually</p>
              </div>
            </PopoverContent>
          </div>

          <Popover>
            <PopoverTrigger>
              <span role="button">I'm a span but can still open a popover</span>
            </PopoverTrigger>
            <PopoverContent>
                    Some content
            </PopoverContent>
          </Popover>

          {/* Rich content example */}
          <Popover>
            <Button variant="outline">User Menu</Button>
            <PopoverContent>
              <div className="min-w-48">
                <div className="border-b border-gray-100 px-3 py-2">
                  <p className="text-sm font-medium">John Doe</p>
                  <p className="text-xs text-gray-500">john@example.com</p>
                </div>
                <div className="py-1">
                  <button className="w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-gray-50">
                    Profile Settings
                  </button>
                  <button className="w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-gray-50">
                    Billing
                  </button>
                  <hr className="my-1" />
                  <button className="w-full rounded-sm px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                    Sign out
                  </button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-lg font-bold">Tooltip</h2>
        <div className="flex gap-4">
          <Tooltip title="This is a helpful tooltip message">
            <Button variant="outline">Tooltip</Button>
          </Tooltip>
          <Tooltip description="This is a helpful tooltip message" title="Tooltip Header">
            <Button variant="outline">Tooltip with Title + Description</Button>
          </Tooltip>
          <Tooltip title="Tooltip Header" arrow={true}>
            <Button variant="outline">Tooltip with Arrow</Button>
          </Tooltip>
        </div>
      </section>

      <HorizontalScrollerDemo />

      <ToastDemo />

      <TabsDemo />
    </div>
  )
}

export default ComponentsRoute
